import { DateTime, Duration } from 'luxon';
import { DateRange } from 'src/app/common/daterange';
import { NewStatsOptions } from '../stats/stats.service';

export const DEMO_CALENDAR_MEETING_COUNT = 5;
const DEMO_CALENDAR_FALLBACK_DAY = DateTime.utc(2024, 3, 15).startOf('day');

export function getDemoDateRangeFromOptions(options: NewStatsOptions): DateRange {
  const dr = DateRange.fromOptions(options);
  return dr.setTimezone(options.timezone || dr.timezone);
}

/** Start time for a mock calendar meeting, anchored to the selected report range. */
export function getDemoCalendarMeetingStart(
  options: { from?: string, to?: string, timezone?: string } = {},
  index: number,
  count = DEMO_CALENDAR_MEETING_COUNT,
): DateTime {
  const zone = options.timezone || 'UTC';
  let startDay = DEMO_CALENDAR_FALLBACK_DAY.setZone(zone, { keepLocalTime: true });
  let dayCount = 1;

  if (options.from) {
    const dr = getDemoDateRangeFromOptions(options);
    startDay = dr.start.setZone(zone).startOf('day');
    dayCount = Math.max(1, Math.round(dr.end.setZone(zone).diff(startDay, 'days').days));
  }

  const dayOffset = dayCount <= 1
    ? 0
    : Math.min(Math.floor((index * dayCount) / count), dayCount - 1);
  // Single-day: keep all meetings on that local calendar day (21:00 + 1h still same day).
  const hour = dayCount <= 1 ? 9 + index * 3 : 9;

  return startDay.plus({ days: dayOffset }).set({
    hour,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}

export function getDemoDateBuckets(options: NewStatsOptions): DateRange[] {
  const dr = getDemoDateRangeFromOptions(options);
  const period = options.period || 'days';
  const interval = options.interval ?? 1;

  return dr.divide(
    Duration.fromObject({ [period]: interval }),
    options.period || true,
  );
}

export function withBucketDateOptions(options: NewStatsOptions, bucket: DateRange): NewStatsOptions {
  return {
    ...options,
    from: bucket.start.toUTC().toISO(),
    to: bucket.end.toUTC().toISO(),
  };
}

export function countDemoCalendarDays(from: DateTime, to: DateTime): number {
  return Math.max(1, Math.ceil(to.diff(from, 'days').days));
}

export function countDemoWorkdays(from: DateTime, to: DateTime): number {
  const start = from.startOf('day');
  const end = to.startOf('day');
  const diffDays = Math.floor(end.diff(start, 'days').days);

  let workdays = 0;
  for (let i = 0; i <= diffDays; i++) {
    const dayOfWeek = start.plus({ days: i }).weekday;
    if (dayOfWeek !== 6 && dayOfWeek !== 7) {
      workdays += 1;
    }
  }

  return Math.max(1, workdays);
}
